import { useState } from 'react';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../context/ToastContext.jsx';
import {
  adminGetCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
  adminGetBrands, adminCreateBrand, adminUpdateBrand, adminDeleteBrand,
  adminGetOptionTypes, adminCreateOptionType, adminUpdateOptionType, adminDeleteOptionType,
} from '../../api/adminCatalogApi.js';

const TABS = [
  { id: 'categories', label: '分類管理' },
  { id: 'brands',     label: '品牌管理' },
  { id: 'options',    label: '選項管理' },
];

function AdminSettingsPage() {
  const [tab, setTab] = useState('categories');

  return (
    <>
      <div className="admin-page-head">
        <h1>設定<small>SETTINGS</small></h1>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--line)' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              font: '700 14px "Noto Sans TC", sans-serif',
              color: tab === t.id ? 'var(--navy)' : 'var(--ink-soft)',
              borderBottom: tab === t.id ? '2px solid var(--navy)' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'categories' && <CategoriesTab />}
      {tab === 'brands'     && <BrandsTab />}
      {tab === 'options'    && <OptionTypesTab />}
    </>
  );
}

// ─── 分類管理 ────────────────────────────────────────────────

const EMPTY_CATEGORY = { id: '', name: '', en: '', displayOrder: 0, active: true };

function CategoriesTab() {
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data }

  const { data: categories = [], loading } = useApi(() => adminGetCategories(), [reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除分類「${name}」？`)) return;
    try {
      await adminDeleteCategory(id);
      showToast(`已刪除：${name}`, 'success');
      reload();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal.mode === 'create') {
        await adminCreateCategory(form);
        showToast('分類已建立', 'success');
      } else {
        await adminUpdateCategory(modal.data.id, form);
        showToast('分類已更新', 'success');
      }
      setModal(null);
      reload();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="admin-btn primary" onClick={() => {
          const nextOrder = categories.length > 0 ? Math.max(...categories.map(c => c.displayOrder)) + 1 : 1;
          setModal({ mode: 'create', data: { ...EMPTY_CATEGORY, displayOrder: nextOrder } });
        }}>
          + 新增分類
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>ID</th>
              <th>名稱</th>
              <th style={{ width: 120 }}>英文名稱</th>
              <th style={{ width: 60, textAlign: 'center' }}>排序</th>
              <th style={{ width: 70 }}>狀態</th>
              <th style={{ width: 120 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : categories.length === 0
                ? <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無分類資料</td></tr>
                : categories.map(c => (
                  <tr key={c.id}>
                    <td style={{ font: '600 12px monospace', color: 'var(--ink-soft)' }}>{c.id}</td>
                    <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{c.name}</td>
                    <td style={{ fontSize: 13 }}>{c.en}</td>
                    <td style={{ textAlign: 'center' }}>{c.displayOrder}</td>
                    <td><span className={`chip ${c.active ? 'active' : 'cancelled'}`}>{c.active ? '啟用' : '停用'}</span></td>
                    <td>
                      <div className="actions">
                        <button className="admin-btn ghost sm" onClick={() => setModal({ mode: 'edit', data: c })}>編輯</button>
                        <button className="admin-btn danger sm" onClick={() => handleDelete(c.id, c.name)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {modal && (
        <CategoryModal
          mode={modal.mode}
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function CategoryModal({ mode, initial, onSave, onClose }) {
  const [form, setForm] = useState({ ...initial });
  const [submitting, setSubmitting] = useState(false);

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSave({ ...form, displayOrder: Number(form.displayOrder) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <h2 style={styles.modalTitle}>{mode === 'create' ? '新增分類' : '編輯分類'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="row2">
            <div className="field">
              <label>ID（slug）*</label>
              <input value={form.id} onChange={e => change('id', e.target.value)}
                placeholder="barrel" disabled={mode === 'edit'} />
            </div>
            <div className="field">
              <label>排序 *</label>
              <input type="number" min="0" step="1" value={form.displayOrder} onChange={e => change('displayOrder', e.target.value)} />
            </div>
          </div>

          <div className="row2">
            <div className="field">
              <label>中文名稱 *</label>
              <input value={form.name} onChange={e => change('name', e.target.value)} placeholder="標身" />
            </div>
            <div className="field">
              <label>英文名稱</label>
              <input value={form.en || ''} onChange={e => change('en', e.target.value)} placeholder="BARREL" />
            </div>
          </div>

          <div className="field">
            <label>狀態 *</label>
            <select value={form.active} onChange={e => change('active', e.target.value === 'true')}>
              <option value="true">啟用</option>
              <option value="false">停用</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" className="admin-btn ghost" onClick={onClose}>取消</button>
            <button type="submit" className="admin-btn primary" disabled={submitting}>
              {submitting ? '儲存中…' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── 品牌管理 ────────────────────────────────────────────────

const EMPTY_BRAND = { id: '', name: '', logoUrl: '', displayOrder: 0, active: true };

function BrandsTab() {
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null);

  const { data: brands = [], loading } = useApi(() => adminGetBrands(), [reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除品牌「${name}」？`)) return;
    try {
      await adminDeleteBrand(id);
      showToast(`已刪除：${name}`, 'success');
      reload();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal.mode === 'create') {
        await adminCreateBrand(form);
        showToast('品牌已建立', 'success');
      } else {
        await adminUpdateBrand(modal.data.id, form);
        showToast('品牌已更新', 'success');
      }
      setModal(null);
      reload();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="admin-btn primary" onClick={() => {
          const nextOrder = brands.length > 0 ? Math.max(...brands.map(b => b.displayOrder)) + 1 : 1;
          setModal({ mode: 'create', data: { ...EMPTY_BRAND, displayOrder: nextOrder } });
        }}>
          + 新增品牌
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>ID</th>
              <th>名稱</th>
              <th style={{ width: 60, textAlign: 'center' }}>排序</th>
              <th style={{ width: 70 }}>狀態</th>
              <th style={{ width: 120 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : brands.length === 0
                ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無品牌資料</td></tr>
                : brands.map(b => (
                  <tr key={b.id}>
                    <td style={{ font: '600 12px monospace', color: 'var(--ink-soft)' }}>{b.id}</td>
                    <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{b.name}</td>
                    <td style={{ textAlign: 'center' }}>{b.displayOrder}</td>
                    <td><span className={`chip ${b.active ? 'active' : 'cancelled'}`}>{b.active ? '啟用' : '停用'}</span></td>
                    <td>
                      <div className="actions">
                        <button className="admin-btn ghost sm" onClick={() => setModal({ mode: 'edit', data: b })}>編輯</button>
                        <button className="admin-btn danger sm" onClick={() => handleDelete(b.id, b.name)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {modal && (
        <BrandModal
          mode={modal.mode}
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function BrandModal({ mode, initial, onSave, onClose }) {
  const [form, setForm] = useState({ ...initial });
  const [submitting, setSubmitting] = useState(false);

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSave({ ...form, displayOrder: Number(form.displayOrder) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <h2 style={styles.modalTitle}>{mode === 'create' ? '新增品牌' : '編輯品牌'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="row2">
            <div className="field">
              <label>ID（slug）*</label>
              <input value={form.id} onChange={e => change('id', e.target.value)}
                placeholder="TARGET" disabled={mode === 'edit'} />
            </div>
            <div className="field">
              <label>排序 *</label>
              <input type="number" min="0" step="1" value={form.displayOrder} onChange={e => change('displayOrder', e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label>品牌名稱 *</label>
            <input value={form.name} onChange={e => change('name', e.target.value)} placeholder="TARGET DARTS" />
          </div>

          <div className="field">
            <label>Logo URL</label>
            <input value={form.logoUrl || ''} onChange={e => change('logoUrl', e.target.value)} placeholder="https://..." />
          </div>

          <div className="field">
            <label>狀態 *</label>
            <select value={form.active} onChange={e => change('active', e.target.value === 'true')}>
              <option value="true">啟用</option>
              <option value="false">停用</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" className="admin-btn ghost" onClick={onClose}>取消</button>
            <button type="submit" className="admin-btn primary" disabled={submitting}>
              {submitting ? '儲存中…' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── 選項類型管理 ────────────────────────────────────────────

const EMPTY_OPTION_TYPE = { name: '', displayOrder: 0, active: true };

function OptionTypesTab() {
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null);

  const { data: optionTypes = [], loading } = useApi(() => adminGetOptionTypes(), [reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除選項類型「${name}」？（已建立的商品不受影響）`)) return;
    try {
      await adminDeleteOptionType(id);
      showToast(`已刪除：${name}`, 'success');
      reload();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal.mode === 'create') {
        await adminCreateOptionType(form);
        showToast('選項類型已建立', 'success');
      } else {
        await adminUpdateOptionType(modal.data.id, form);
        showToast('選項類型已更新', 'success');
      }
      setModal(null);
      reload();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          這裡維護「選項維度」名稱（如重量、鏢尖、顏色）。發布商品時從這份清單挑選，值在商品自己填。
        </p>
        <button className="admin-btn primary" onClick={() => {
          const nextOrder = optionTypes.length > 0 ? Math.max(...optionTypes.map(o => o.displayOrder)) + 1 : 1;
          setModal({ mode: 'create', data: { ...EMPTY_OPTION_TYPE, displayOrder: nextOrder } });
        }}>
          + 新增選項
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>名稱</th>
              <th style={{ width: 60, textAlign: 'center' }}>排序</th>
              <th style={{ width: 70 }}>狀態</th>
              <th style={{ width: 120 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : optionTypes.length === 0
                ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無選項類型，先新增一個（如「重量」）</td></tr>
                : optionTypes.map(o => (
                  <tr key={o.id}>
                    <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{o.name}</td>
                    <td style={{ textAlign: 'center' }}>{o.displayOrder}</td>
                    <td><span className={`chip ${o.active ? 'active' : 'cancelled'}`}>{o.active ? '啟用' : '停用'}</span></td>
                    <td>
                      <div className="actions">
                        <button className="admin-btn ghost sm" onClick={() => setModal({ mode: 'edit', data: o })}>編輯</button>
                        <button className="admin-btn danger sm" onClick={() => handleDelete(o.id, o.name)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {modal && (
        <OptionTypeModal
          mode={modal.mode}
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function OptionTypeModal({ mode, initial, onSave, onClose }) {
  const [form, setForm] = useState({ ...initial });
  const [submitting, setSubmitting] = useState(false);

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await onSave({ ...form, name: form.name.trim(), displayOrder: Number(form.displayOrder) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <h2 style={styles.modalTitle}>{mode === 'create' ? '新增選項類型' : '編輯選項類型'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="field">
            <label>名稱 *</label>
            <input value={form.name} onChange={e => change('name', e.target.value)} placeholder="重量" />
          </div>

          <div className="row2">
            <div className="field">
              <label>排序 *</label>
              <input type="number" min="0" step="1" value={form.displayOrder} onChange={e => change('displayOrder', e.target.value)} />
            </div>
            <div className="field">
              <label>狀態 *</label>
              <select value={form.active} onChange={e => change('active', e.target.value === 'true')}>
                <option value="true">啟用</option>
                <option value="false">停用</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="admin-btn ghost" onClick={onClose}>取消</button>
            <button type="submit" className="admin-btn primary" disabled={submitting}>
              {submitting ? '儲存中…' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal 共用樣式 ───────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(14, 28, 64, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--white)',
    borderRadius: 14,
    padding: '28px 32px',
    width: '90%',
    maxWidth: 640,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(14,28,64,0.18)',
  },
  modalHead: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 24,
  },
  modalTitle: {
    font: '800 20px "Noto Sans TC", sans-serif',
    color: 'var(--navy)', margin: 0,
  },
  closeBtn: {
    background: 'none', border: 'none',
    font: '600 18px Inter', color: 'var(--ink-soft)',
    cursor: 'pointer', padding: '4px 8px',
  },
};

export default AdminSettingsPage;
